import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { QRCodeSVG } from 'qrcode.react';
import { AuthShell } from '@/components/AuthShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { HubClientError } from '@verybigthings/trackframe-hub-client';
import { hub } from '@/lib/hub-client';

/**
 * HUB-121 D8 — `/login/mfa`, the second step of a sign-in.
 *
 * Two ways in: a JSON login answered `202` and the Login page navigated here
 * with the challenge in router state; or the OAuth callback redirected here
 * with the challenge in the HttpOnly `tf_mfa` cookie (no state — the hub
 * reads the cookie when the body carries no token). `enrollmentRequired`
 * in the state, or `?enroll=1` on the cookie path, (D6) swaps the code form
 * for the enrolment wizard, authenticated by the same challenge.
 */

export interface MfaRouteState {
  challengeToken?: string;
  enrollmentRequired?: boolean;
  returnTo?: string | null;
}

export const EXPIRED_MESSAGE = 'This sign-in attempt has expired. Please sign in again.';
export const INVALID_CODE_MESSAGE = 'That code is not valid.';
export const REUSED_CODE_MESSAGE = 'That code was already used — wait for the next one.';

/** Maps a hub error code to the copy the user sees; `fallback` for the rest. */
export function mfaErrorMessage(code: string | undefined, fallback: string): string {
  switch (code) {
    case 'mfa_invalid_code': return INVALID_CODE_MESSAGE;
    case 'mfa_code_reused': return REUSED_CODE_MESSAGE;
    case 'challenge_expired': return EXPIRED_MESSAGE;
    default: return fallback;
  }
}

function errorOf(err: unknown, fallback: string): { message: string; expired: boolean } {
  const code = err instanceof HubClientError ? err.code : undefined;
  return { message: mfaErrorMessage(code, (err as Error)?.message ?? fallback), expired: code === 'challenge_expired' };
}

/** One input for a six-digit code, or a `xxxxx-xxxxx` recovery code. */
export function CodeInput({
  id,
  value,
  onChange,
  recovery,
  autoFocus,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  recovery: boolean;
  autoFocus?: boolean;
}) {
  return (
    <Input
      id={id}
      type="text"
      inputMode={recovery ? 'text' : 'numeric'}
      autoComplete="one-time-code"
      required
      minLength={6}
      maxLength={16}
      placeholder={recovery ? 'xxxxx-xxxxx' : '123456'}
      autoFocus={autoFocus}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/** The QR code + manual secret a user scans into their authenticator app. */
export function EnrolScan({ otpauthUri, secret }: { otpauthUri: string; secret: string }) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Scan this with an authenticator app (Google Authenticator, Microsoft Authenticator, 1Password, …), then enter the six-digit code it shows.
      </p>
      <div className="flex justify-center rounded-md border bg-white p-3">
        <QRCodeSVG value={otpauthUri} size={176} aria-label="Authenticator QR code" />
      </div>
      <p className="text-xs text-muted-foreground">
        Can’t scan? Enter this key by hand: <code data-testid="totp-secret" className="select-all font-mono">{secret}</code>
      </p>
    </div>
  );
}

/** Recovery codes are shown exactly once. */
export function RecoveryCodesPanel({ codes, onContinue, continueLabel = 'Continue' }: { codes: string[]; onContinue: () => void; continueLabel?: string }) {
  return (
    <div className="space-y-3">
      <p className="text-sm">
        Save these recovery codes somewhere safe. Each one signs you in once if you lose your phone. They are shown only now.
      </p>
      <ul data-testid="recovery-codes" className="grid grid-cols-2 gap-1 rounded-md border bg-muted/40 p-3 font-mono text-sm">
        {codes.map((c) => <li key={c}>{c}</li>)}
      </ul>
      <Button type="button" className="w-full" onClick={onContinue}>{continueLabel}</Button>
    </div>
  );
}

export function MfaVerifyForm({ challengeToken, returnTo }: { challengeToken?: string; returnTo?: string | null }) {
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      const res = await hub.authMfaVerify({ body: { ...(challengeToken ? { challengeToken } : {}), code } });
      window.location.replace(res.redirectTo ?? returnTo ?? '/');
    } catch (err) {
      const { message, expired: gone } = errorOf(err, 'Could not verify the code');
      setError(message);
      setExpired(gone);
      setPending(false);
    }
  };

  return (
    <AuthShell title="Enter your code" subtitle="Trackframe">
      <form className="space-y-4" onSubmit={onSubmit}>
        <div className="space-y-2">
          <Label htmlFor="mfa-code">{recovery ? 'Recovery code' : 'Code from your authenticator app'}</Label>
          <CodeInput id="mfa-code" value={code} onChange={setCode} recovery={recovery} autoFocus />
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        {expired ? (
          <Link to="/login" className="block text-center text-sm underline underline-offset-4">Sign in again</Link>
        ) : (
          <Button type="submit" className="w-full" disabled={pending}>
            {pending ? 'Checking…' : 'Continue'}
          </Button>
        )}
        <button
          type="button"
          className="w-full text-center text-xs text-muted-foreground underline-offset-4 hover:underline"
          onClick={() => { setRecovery((r) => !r); setCode(''); setError(null); }}
        >
          {recovery ? 'Use my authenticator app instead' : 'Use a recovery code instead'}
        </button>
      </form>
    </AuthShell>
  );
}

type WizardPhase =
  | { kind: 'loading' }
  | { kind: 'scan'; otpauthUri: string; secret: string }
  | { kind: 'codes'; codes: string[]; landing: string }
  | { kind: 'expired'; message: string };

/** D6: the forced enrolment, authenticated by the challenge token; confirm signs the user in. */
export function MfaEnrolWizard({ challengeToken, returnTo }: { challengeToken?: string; returnTo?: string | null }) {
  const [phase, setPhase] = useState<WizardPhase>({ kind: 'loading' });
  const [code, setCode] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    hub.authMfaTotpEnroll({ body: challengeToken ? { challengeToken } : {} })
      .then((r) => { if (!cancelled) setPhase({ kind: 'scan', otpauthUri: r.otpauthUri, secret: r.secret }); })
      .catch((err) => { if (!cancelled) setPhase({ kind: 'expired', message: errorOf(err, 'Could not start the setup').message }); });
    return () => { cancelled = true; };
  }, [challengeToken]);

  const onConfirm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      const res = await hub.authMfaTotpConfirm({ body: { ...(challengeToken ? { challengeToken } : {}), code } });
      setPhase({ kind: 'codes', codes: res.recoveryCodes, landing: res.session?.redirectTo ?? returnTo ?? '/' });
    } catch (err) {
      const { message, expired } = errorOf(err, 'Could not confirm the code');
      if (expired) setPhase({ kind: 'expired', message });
      else setError(message);
      setPending(false);
    }
  };

  return (
    <AuthShell title="Set up your authenticator app" subtitle="Your administrator requires a second factor to sign in.">
      {phase.kind === 'loading' && <p className="text-sm text-muted-foreground">Preparing…</p>}
      {phase.kind === 'expired' && (
        <div className="space-y-3">
          <p className="text-sm text-destructive">{phase.message}</p>
          <Link to="/login" className="block text-center text-sm underline underline-offset-4">Sign in again</Link>
        </div>
      )}
      {phase.kind === 'scan' && (
        <form className="space-y-4" onSubmit={onConfirm}>
          <EnrolScan otpauthUri={phase.otpauthUri} secret={phase.secret} />
          <div className="space-y-2">
            <Label htmlFor="mfa-confirm-code">Six-digit code</Label>
            <CodeInput id="mfa-confirm-code" value={code} onChange={setCode} recovery={false} autoFocus />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button type="submit" className="w-full" disabled={pending}>
            {pending ? 'Checking…' : 'Confirm and sign in'}
          </Button>
        </form>
      )}
      {phase.kind === 'codes' && (
        <RecoveryCodesPanel codes={phase.codes} onContinue={() => window.location.replace(phase.landing)} continueLabel="I saved them — continue" />
      )}
    </AuthShell>
  );
}

/**
 * Route element: picks the form from router state, or from `?enroll=1` when
 * there is none (the OAuth cookie path — the token stays in the cookie).
 */
export function MfaPage() {
  const location = useLocation();
  const state = (location.state ?? {}) as MfaRouteState;
  const enrol = state.enrollmentRequired ?? new URLSearchParams(location.search).get('enroll') === '1';
  if (enrol) {
    return <MfaEnrolWizard challengeToken={state.challengeToken} returnTo={state.returnTo} />;
  }
  return <MfaVerifyForm challengeToken={state.challengeToken} returnTo={state.returnTo} />;
}
